import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiUrl, fmtFecha } from '../utils/api';
import { Spinner, Empty, ConfirmModal } from '../components/UI';
import NuevoClienteForm from '../components/NuevoClienteForm';

const ESTADOS = [
  { val: 'B', label: 'B' },
  { val: 'R', label: 'R' },
  { val: 'M', label: 'M' },
  { val: 'NA', label: 'N/A' },
];

// Pasa los datos de un vehículo de la ficha del cliente a los campos del chequeo.
// Con soloVacios = true no pisa lo que ya esté escrito en el chequeo.
function aplicarVehiculo(prev, v, soloVacios) {
  const campos = ['marca', 'modelo', 'anio', 'matricula', 'combustible', 'transmision',
                  'n_chasis', 'n_motor', 'motor', 'admision', 'color'];
  const nuevo = { ...prev };
  campos.forEach(c => {
    const valor = v[c] != null ? String(v[c]) : '';
    if (!valor) return;
    if (soloVacios && prev[c]) return;
    nuevo[c] = valor;
  });
  return nuevo;
}

function etiquetaVehiculo(v) {
  const base = [v.marca, v.modelo, v.anio ? `(${v.anio})` : ''].filter(Boolean).join(' ') || 'Vehículo sin datos';
  const mat = v.matricula ? ` — ${v.matricula}` : '';
  const estado = v.estado_venta === 'vendido' ? ' · vendido' : v.estado_venta === 'en_evaluacion' ? ' · en evaluación' : '';
  return `${base}${mat}${estado}`;
}

function ElegirVehiculo({ vehiculos, onElegir, titulo }) {
  return (
    <div style={{ marginTop: '.6rem', padding: '.6rem', background: 'var(--azul-claro)', borderRadius: 'var(--radio)' }}>
      <p style={{ fontSize: '.8125rem', marginBottom: '.4rem', fontWeight: 600 }}>{titulo}</p>
      {vehiculos.map(v => (
        <button key={v.id} type="button" className="btn btn-outline btn-sm"
          style={{ justifyContent: 'flex-start', width: '100%', marginBottom: '.3rem' }}
          onClick={() => onElegir(v)}>
          🚗 {etiquetaVehiculo(v)}
        </button>
      ))}
    </div>
  );
}

export default function ChequeoPrecompra() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [creando, setCreando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [busquedaLista, setBusquedaLista] = useState('');

  const cargar = () => {
    setLoading(true);
    api('/chequeos/').then(res => { setLista(res); setLoading(false); });
  };

  useEffect(() => { cargar(); }, []);

  const verDetalle = async (id) => {
    const res = await api(`/chequeos/${id}`);
    setDetalle(res);
  };

  const eliminar = async () => {
    await api(`/chequeos/${eliminandoId}`, { method: 'DELETE' });
    setEliminandoId(null);
    cargar();
  };

  const reabrirDesdeLista = async (id) => {
    await api(`/chequeos/${id}/reabrir`, { method: 'POST' });
    cargar();
  };

  const q = busquedaLista.trim().toLowerCase();
  const listaFiltrada = !q ? lista : lista.filter(c => (
    c.id.toLowerCase().includes(q) ||
    (c.nombre_cliente || '').toLowerCase().includes(q) ||
    (c.marca || '').toLowerCase().includes(q) ||
    (c.modelo || '').toLowerCase().includes(q) ||
    (c.matricula || '').toLowerCase().includes(q)
  ));

  if (creando) return (
    <FormularioChequeo
      onCreado={(id) => { setCreando(false); cargar(); verDetalle(id); }}
      onCancelar={() => setCreando(false)}
    />
  );

  if (detalle) return (
    <DetalleChequeo
      chequeo={detalle}
      onVolver={() => { setDetalle(null); cargar(); }}
      onActualizado={(d) => setDetalle(d)}
    />
  );

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Chequeo precompra</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setCreando(true)}>+ Nuevo chequeo</button>
      </div>

      {lista.length > 0 && (
        <input className="form-input" placeholder="🔍 Buscar por cliente, marca, modelo, matrícula o N° de orden..."
          value={busquedaLista} onChange={e => setBusquedaLista(e.target.value)}
          style={{ marginBottom: '.75rem' }} />
      )}

      {loading ? <Spinner texto="Cargando..." /> :
        lista.length === 0 ? <Empty icon="🔍" texto="Sin chequeos todavía" /> :
        listaFiltrada.length === 0 ? <Empty icon="🔍" texto="No hay chequeos que coincidan con la búsqueda" /> :
        <div className="tabla-wrap">
          <table className="tabla">
            <thead>
              <tr><th>ID</th><th>Fecha</th><th>Interesado</th><th>Vehículo</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {listaFiltrada.map(c => (
                <tr key={c.id}>
                  <td><span className="badge badge-gris">{c.id}</span></td>
                  <td>{fmtFecha(c.fecha)}</td>
                  <td style={{ fontWeight: 500 }}>{c.nombre_cliente}</td>
                  <td>{c.marca} {c.modelo} {c.matricula ? `| ${c.matricula}` : ''}</td>
                  <td>
                    {c.cerrado
                      ? <span className="badge badge-gris">Cerrada</span>
                      : <span className="badge badge-verde">Abierta</span>}
                  </td>
                  <td style={{ display: 'flex', gap: '.4rem' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => verDetalle(c.id)}>Ver</button>
                    {!!c.cerrado && (
                      <button className="btn btn-outline btn-sm" onClick={() => reabrirDesdeLista(c.id)}>↺ Reabrir</button>
                    )}
                    <button className="btn btn-outline btn-sm" style={{ color: 'var(--rojo)' }}
                      onClick={() => setEliminandoId(c.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {eliminandoId && (
        <ConfirmModal
          mensaje="¿Eliminar este chequeo?"
          textoConfirmar="Eliminar"
          onConfirm={eliminar}
          onCancel={() => setEliminandoId(null)}
        />
      )}
    </div>
  );
}

function FormularioChequeo({ onCreado, onCancelar }) {
  const [cliente, setCliente] = useState(null);
  const [modoNuevo, setModoNuevo] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [vehiculosCliente, setVehiculosCliente] = useState([]);

  const [veh, setVeh] = useState({
    vendedor_nombre: '', vendedor_contacto: '',
    marca: '', modelo: '', anio: '', version: '', color: '', kilometraje: '',
    combustible: '', transmision: '', matricula: '', padron: '', n_chasis: '', n_motor: '', ultimo_service: '',
    motor: '', admision: '',
  });
  const [fecha, setFecha] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Montevideo' }));

  const [items, setItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [leyendoFoto, setLeyendoFoto] = useState(false);

  useEffect(() => {
    api('/chequeos/items-base').then(setItems);
  }, []);

  useEffect(() => {
    if (busqueda.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const res = await api(`/clientes?q=${encodeURIComponent(busqueda)}`);
      setResultados(res);
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const actualizarItem = (idx, campo, valor) => {
    setItems(arr => arr.map((it, i) => i === idx ? { ...it, [campo]: valor } : it));
  };

  // Al elegir un cliente que ya existe: traer los datos del vehículo que ya tiene cargado
  const seleccionarCliente = async (c) => {
    setCliente(c); setResultados([]); setBusqueda(''); setVehiculosCliente([]);
    try {
      const lista = await api(`/vehiculos/cliente/${c.id}`);
      if (lista.length === 1) {
        setVeh(prev => aplicarVehiculo(prev, lista[0], false));
      } else if (lista.length > 1) {
        setVehiculosCliente(lista);
      }
    } catch {
      // si falla, se puede cargar a mano igual
    }
  };

  const leerFoto = async (file) => {
    if (!file) return;
    setLeyendoFoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(apiUrl('/chequeos/leer-foto'), { method: 'POST', body: formData });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(data.items);
    } catch {
      alert('No se pudo leer la foto. Probá con otra más clara, o cargá a mano.');
    }
    setLeyendoFoto(false);
  };

  const guardar = async () => {
    if (!cliente) { setError('Elegí o creá un cliente primero.'); return; }
    setGuardando(true); setError('');
    try {
      const res = await api('/chequeos/', {
        method: 'POST',
        body: JSON.stringify({
          id_cliente: cliente.id,
          fecha,
          ...veh,
          observaciones_generales: observaciones,
          items,
        })
      });
      onCreado(res.id);
    } catch (e) {
      setError(e.message);
    }
    setGuardando(false);
  };

  const categorias = [...new Set(items.map(i => i.categoria))];

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Nuevo chequeo precompra</h1>
        <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Cancelar</button>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Interesado (cliente)</p>
        {modoNuevo && <NuevoClienteForm onCreado={(c, v) => {
          setCliente(c);
          setModoNuevo(false);
          if (v) {
            setVeh(prev => ({
              ...prev,
              marca: v.marca || prev.marca,
              modelo: v.modelo || prev.modelo,
              anio: v.anio || prev.anio,
              matricula: v.matricula || prev.matricula,
              combustible: v.combustible || prev.combustible,
              transmision: v.transmision || prev.transmision,
              n_chasis: v.n_chasis || prev.n_chasis,
              n_motor: v.n_motor || prev.n_motor,
              motor: v.motor || prev.motor,
              admision: v.admision || prev.admision,
            }));
            if (v.notas_vehiculo) {
              setObservaciones(prev => prev ? `${prev}\n${v.notas_vehiculo}` : v.notas_vehiculo);
            }
          }
        }} onCancelar={() => setModoNuevo(false)} />}
        {!modoNuevo && !cliente && (
          <>
            <div className="sec-header" style={{ marginBottom: '.5rem' }}>
              <input className="form-input" placeholder="Buscar cliente..." value={busqueda}
                onChange={e => setBusqueda(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-outline btn-sm" onClick={() => setModoNuevo(true)}>+ Nuevo</button>
            </div>
            {resultados.map(c => (
              <button key={c.id} className="btn btn-outline btn-sm" style={{ justifyContent: 'flex-start', width: '100%', marginBottom: '.3rem' }}
                onClick={() => seleccionarCliente(c)}>
                {c.nombre} {c.celular ? `— ${c.celular}` : ''}
              </button>
            ))}
          </>
        )}
        {!modoNuevo && cliente && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontWeight: 600 }}>{cliente.nombre}</p>
            <button className="btn btn-outline btn-sm" onClick={() => { setCliente(null); setVehiculosCliente([]); }}>Cambiar</button>
          </div>
        )}
        {!modoNuevo && cliente && vehiculosCliente.length > 0 && (
          <ElegirVehiculo
            vehiculos={vehiculosCliente}
            titulo="Este cliente tiene más de un vehículo. ¿Cuál es el del chequeo?"
            onElegir={(v) => { setVeh(prev => aplicarVehiculo(prev, v, false)); setVehiculosCliente([]); }}
          />
        )}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Fecha del chequeo</p>
        <input type="date" className="form-input" value={fecha} onChange={e => setFecha(e.target.value)} style={{ maxWidth: 220 }} />
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Cargar desde foto de la planilla (opcional)</p>
        <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.6rem' }}>
          Sacale una foto bien derecha y con buena luz a la hoja ya completada a mano. La IA va a precargar los casilleros — después revisá y corregí lo que haga falta.
        </p>
        <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
          {leyendoFoto ? '... leyendo' : '📷 Cargar foto de la planilla'}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            disabled={leyendoFoto} onChange={e => leerFoto(e.target.files[0])} />
        </label>
        {leyendoFoto && <Spinner texto="Leyendo planilla con IA..." />}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vendedor (opcional)</p>
        <div className="form-grid-2">
          <input className="form-input" placeholder="Nombre" value={veh.vendedor_nombre}
            onChange={e => setVeh(v => ({ ...v, vendedor_nombre: e.target.value }))} />
          <input className="form-input" placeholder="Contacto" value={veh.vendedor_contacto}
            onChange={e => setVeh(v => ({ ...v, vendedor_contacto: e.target.value }))} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vehículo</p>
        <div className="form-grid-2" style={{ rowGap: '.5rem' }}>
          <input className="form-input" placeholder="Marca" value={veh.marca} onChange={e => setVeh(v => ({ ...v, marca: e.target.value }))} />
          <input className="form-input" placeholder="Modelo" value={veh.modelo} onChange={e => setVeh(v => ({ ...v, modelo: e.target.value }))} />
          <input className="form-input" placeholder="Año" value={veh.anio} onChange={e => setVeh(v => ({ ...v, anio: e.target.value }))} />
          <input className="form-input" placeholder="Versión" value={veh.version} onChange={e => setVeh(v => ({ ...v, version: e.target.value }))} />
          <input className="form-input" placeholder="Color" value={veh.color} onChange={e => setVeh(v => ({ ...v, color: e.target.value }))} />
          <input className="form-input" placeholder="Kilometraje" value={veh.kilometraje} onChange={e => setVeh(v => ({ ...v, kilometraje: e.target.value }))} />
          <input className="form-input" placeholder="Combustible" value={veh.combustible} onChange={e => setVeh(v => ({ ...v, combustible: e.target.value }))} />
          <input className="form-input" placeholder="Transmisión" value={veh.transmision} onChange={e => setVeh(v => ({ ...v, transmision: e.target.value }))} />
          <input className="form-input" placeholder="Motor (cilindrada)" value={veh.motor} onChange={e => setVeh(v => ({ ...v, motor: e.target.value }))} />
          <input className="form-input" placeholder="Admisión" value={veh.admision} onChange={e => setVeh(v => ({ ...v, admision: e.target.value }))} />
          <input className="form-input" placeholder="Matrícula" value={veh.matricula} onChange={e => setVeh(v => ({ ...v, matricula: e.target.value }))} />
          <input className="form-input" placeholder="Padrón" value={veh.padron} onChange={e => setVeh(v => ({ ...v, padron: e.target.value }))} />
          <input className="form-input" placeholder="N° de chasis (VIN)" value={veh.n_chasis} onChange={e => setVeh(v => ({ ...v, n_chasis: e.target.value }))} />
          <input className="form-input" placeholder="N° de motor" value={veh.n_motor} onChange={e => setVeh(v => ({ ...v, n_motor: e.target.value }))} />
        </div>
        <textarea className="form-textarea" placeholder="Último service / mantenimiento / reparaciones registradas"
          style={{ marginTop: '.5rem' }} rows={2} value={veh.ultimo_service}
          onChange={e => setVeh(v => ({ ...v, ultimo_service: e.target.value }))} />
      </div>

      <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.75rem' }}>
        Referencia: <b>B</b> = Bueno · <b>R</b> = Regular · <b>M</b> = Malo · <b>N/A</b> = No aplica
      </p>

      {categorias.map(categoria => (
        <div className="card" key={categoria} style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>{categoria}</p>
          {items.map((it, idx) => it.categoria === categoria && (
            <div key={idx} style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.5rem', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 200px', fontSize: '.875rem' }}>{it.sistema}</span>
              <div style={{ display: 'flex', gap: '.25rem' }}>
                {ESTADOS.map(e => (
                  <button key={e.val} type="button"
                    className={it.estado === e.val ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                    style={{ padding: '.3rem .55rem', minWidth: 38 }}
                    onClick={() => actualizarItem(idx, 'estado', e.val)}>
                    {e.label}
                  </button>
                ))}
              </div>
              <input className="form-input" placeholder="Observación" style={{ flex: '2 1 180px', fontSize: '.8125rem' }}
                value={it.observacion} onChange={e => actualizarItem(idx, 'observacion', e.target.value)} />
            </div>
          ))}
        </div>
      ))}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Observaciones generales</p>
        <textarea className="form-textarea" placeholder="Observaciones generales" rows={8}
          value={observaciones} onChange={e => setObservaciones(e.target.value)} />
      </div>

      {error && <p style={{ color: 'var(--rojo)', marginBottom: '.75rem', fontSize: '.875rem' }}>{error}</p>}
      {guardando && <Spinner texto="Guardando..." />}

      <button className="btn btn-primary btn-full" disabled={guardando} onClick={guardar}>
        ✅ Aceptar y guardar chequeo
      </button>
    </div>
  );
}

function EditarChequeo({ chequeo, onGuardado, onCancelar }) {
  const [cli, setCli] = useState({
    nombre: chequeo.nombre_cliente || '', celular: chequeo.celular || '',
    rut: chequeo.rut || '', direccion: chequeo.direccion || '',
  });
  const [veh, setVeh] = useState({
    vendedor_nombre: chequeo.vendedor_nombre || '', vendedor_contacto: chequeo.vendedor_contacto || '',
    marca: chequeo.marca || '', modelo: chequeo.modelo || '', anio: chequeo.anio || '',
    version: chequeo.version || '', color: chequeo.color || '', kilometraje: chequeo.kilometraje || '',
    combustible: chequeo.combustible || '', transmision: chequeo.transmision || '',
    matricula: chequeo.matricula || '', padron: chequeo.padron || '',
    n_chasis: chequeo.n_chasis || '', n_motor: chequeo.n_motor || '', ultimo_service: chequeo.ultimo_service || '',
    motor: chequeo.motor || '', admision: chequeo.admision || '',
  });
  const [items, setItems] = useState(chequeo.items);
  const [observaciones, setObservaciones] = useState(chequeo.observaciones_generales || '');
  const [fecha, setFecha] = useState(chequeo.fecha || '');
  const [guardando, setGuardando] = useState(false);
  const [leyendoFoto, setLeyendoFoto] = useState(false);
  const [vehiculosCliente, setVehiculosCliente] = useState([]);
  const [trayendoVehiculo, setTrayendoVehiculo] = useState(false);

  const actualizarItem = (idx, campo, valor) => {
    setItems(arr => arr.map((it, i) => i === idx ? { ...it, [campo]: valor } : it));
  };

  // Completa los campos VACÍOS del vehículo con los de la ficha del cliente (no pisa lo ya escrito)
  const traerVehiculoDelCliente = async () => {
    setTrayendoVehiculo(true);
    try {
      const lista = await api(`/vehiculos/cliente/${chequeo.id_cliente}`);
      if (lista.length === 0) {
        alert('Este cliente no tiene ningún vehículo cargado en su ficha.');
      } else if (lista.length === 1) {
        setVeh(prev => aplicarVehiculo(prev, lista[0], true));
      } else {
        setVehiculosCliente(lista);
      }
    } catch {
      alert('No se pudieron traer los datos del vehículo.');
    }
    setTrayendoVehiculo(false);
  };

  const leerFoto = async (file) => {
    if (!file) return;
    setLeyendoFoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(apiUrl('/chequeos/leer-foto'), { method: 'POST', body: formData });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(data.items);
    } catch {
      alert('No se pudo leer la foto. Probá con otra más clara, o cargá a mano.');
    }
    setLeyendoFoto(false);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      if (chequeo.id_cliente) {
        await api(`/clientes/${chequeo.id_cliente}`, {
          method: 'PATCH',
          body: JSON.stringify({
            nombre: cli.nombre,
            celular: cli.celular,
            rut: cli.rut,
            direccion: cli.direccion,
          })
        });
      }
      await api(`/chequeos/${chequeo.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          id_cliente: chequeo.id_cliente,
          fecha,
          ...veh,
          observaciones_generales: observaciones,
          items,
        })
      });
      onGuardado();
    } catch {
      alert('No se pudo guardar.');
    }
    setGuardando(false);
  };

  const categorias = [...new Set(items.map(i => i.categoria))];

  return (
    <div className="page-content">
      <div className="sec-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Editar {chequeo.id}</h1>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <a href={apiUrl(`/chequeos/${chequeo.id}/pdf-formulario`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
            🖨️ Imprimir formulario en blanco
          </a>
          <button className="btn btn-secondary btn-sm" onClick={onCancelar}>← Cancelar</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Fecha del chequeo</p>
        <input type="date" className="form-input" value={fecha} onChange={e => setFecha(e.target.value)} style={{ maxWidth: 220 }} />
      </div>

      {chequeo.id_cliente && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del cliente</p>
          <div className="form-grid-2" style={{ rowGap: '.5rem' }}>
            <input className="form-input" placeholder="Nombre completo" value={cli.nombre}
              onChange={e => setCli(c => ({ ...c, nombre: e.target.value }))} />
            <input className="form-input" placeholder="Celular" value={cli.celular}
              onChange={e => setCli(c => ({ ...c, celular: e.target.value }))} />
            <input className="form-input" placeholder="RUT o CI" value={cli.rut}
              onChange={e => setCli(c => ({ ...c, rut: e.target.value }))} />
            <input className="form-input" placeholder="Dirección" value={cli.direccion}
              onChange={e => setCli(c => ({ ...c, direccion: e.target.value }))} />
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Cargar desde foto de la planilla (opcional)</p>
        <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
          {leyendoFoto ? '... leyendo' : '📷 Cargar foto de la planilla'}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            disabled={leyendoFoto} onChange={e => leerFoto(e.target.files[0])} />
        </label>
        {leyendoFoto && <Spinner texto="Leyendo planilla con IA..." />}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vendedor (opcional)</p>
        <div className="form-grid-2">
          <input className="form-input" placeholder="Nombre" value={veh.vendedor_nombre}
            onChange={e => setVeh(v => ({ ...v, vendedor_nombre: e.target.value }))} />
          <input className="form-input" placeholder="Contacto" value={veh.vendedor_contacto}
            onChange={e => setVeh(v => ({ ...v, vendedor_contacto: e.target.value }))} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="sec-header" style={{ marginBottom: '.6rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--azul)' }}>Datos del vehículo</p>
          {chequeo.id_cliente && (
            <button type="button" className="btn btn-outline btn-sm" disabled={trayendoVehiculo} onClick={traerVehiculoDelCliente}>
              {trayendoVehiculo ? '...' : '🚗 Traer datos del vehículo del cliente'}
            </button>
          )}
        </div>
        {vehiculosCliente.length > 0 && (
          <div style={{ marginBottom: '.6rem' }}>
            <ElegirVehiculo
              vehiculos={vehiculosCliente}
              titulo="Este cliente tiene más de un vehículo. ¿Cuál querés traer?"
              onElegir={(v) => { setVeh(prev => aplicarVehiculo(prev, v, true)); setVehiculosCliente([]); }}
            />
          </div>
        )}
        <div className="form-grid-2" style={{ rowGap: '.5rem' }}>
          <input className="form-input" placeholder="Marca" value={veh.marca} onChange={e => setVeh(v => ({ ...v, marca: e.target.value }))} />
          <input className="form-input" placeholder="Modelo" value={veh.modelo} onChange={e => setVeh(v => ({ ...v, modelo: e.target.value }))} />
          <input className="form-input" placeholder="Año" value={veh.anio} onChange={e => setVeh(v => ({ ...v, anio: e.target.value }))} />
          <input className="form-input" placeholder="Versión" value={veh.version} onChange={e => setVeh(v => ({ ...v, version: e.target.value }))} />
          <input className="form-input" placeholder="Color" value={veh.color} onChange={e => setVeh(v => ({ ...v, color: e.target.value }))} />
          <input className="form-input" placeholder="Kilometraje" value={veh.kilometraje} onChange={e => setVeh(v => ({ ...v, kilometraje: e.target.value }))} />
          <input className="form-input" placeholder="Combustible" value={veh.combustible} onChange={e => setVeh(v => ({ ...v, combustible: e.target.value }))} />
          <input className="form-input" placeholder="Transmisión" value={veh.transmision} onChange={e => setVeh(v => ({ ...v, transmision: e.target.value }))} />
          <input className="form-input" placeholder="Motor (cilindrada)" value={veh.motor} onChange={e => setVeh(v => ({ ...v, motor: e.target.value }))} />
          <input className="form-input" placeholder="Admisión" value={veh.admision} onChange={e => setVeh(v => ({ ...v, admision: e.target.value }))} />
          <input className="form-input" placeholder="Matrícula" value={veh.matricula} onChange={e => setVeh(v => ({ ...v, matricula: e.target.value }))} />
          <input className="form-input" placeholder="Padrón" value={veh.padron} onChange={e => setVeh(v => ({ ...v, padron: e.target.value }))} />
          <input className="form-input" placeholder="N° de chasis (VIN)" value={veh.n_chasis} onChange={e => setVeh(v => ({ ...v, n_chasis: e.target.value }))} />
          <input className="form-input" placeholder="N° de motor" value={veh.n_motor} onChange={e => setVeh(v => ({ ...v, n_motor: e.target.value }))} />
        </div>
        <textarea className="form-textarea" placeholder="Último service / mantenimiento / reparaciones registradas"
          style={{ marginTop: '.5rem' }} rows={2} value={veh.ultimo_service}
          onChange={e => setVeh(v => ({ ...v, ultimo_service: e.target.value }))} />
      </div>

      <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.75rem' }}>
        Referencia: <b>B</b> = Bueno · <b>R</b> = Regular · <b>M</b> = Malo · <b>N/A</b> = No aplica
      </p>

      {categorias.map(categoria => (
        <div className="card" key={categoria} style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>{categoria}</p>
          {items.map((it, idx) => it.categoria === categoria && (
            <div key={idx} style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.5rem', flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 200px', fontSize: '.875rem' }}>{it.sistema}</span>
              <div style={{ display: 'flex', gap: '.25rem' }}>
                {ESTADOS.map(e => (
                  <button key={e.val} type="button"
                    className={it.estado === e.val ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                    style={{ padding: '.3rem .55rem', minWidth: 38 }}
                    onClick={() => actualizarItem(idx, 'estado', e.val)}>
                    {e.label}
                  </button>
                ))}
              </div>
              <input className="form-input" placeholder="Observación" style={{ flex: '2 1 180px', fontSize: '.8125rem' }}
                value={it.observacion} onChange={e => actualizarItem(idx, 'observacion', e.target.value)} />
            </div>
          ))}
        </div>
      ))}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Observaciones generales</p>
        <textarea className="form-textarea" placeholder="Observaciones generales" rows={8}
          value={observaciones} onChange={e => setObservaciones(e.target.value)} />
      </div>

      {guardando && <Spinner texto="Guardando..." />}

      <button className="btn btn-primary btn-full" disabled={guardando} onClick={guardar}>
        ✅ Aceptar y guardar cambios
      </button>
    </div>
  );
}

function DetalleChequeo({ chequeo, onVolver, onActualizado }) {
  const navigate = useNavigate();
  const [armando, setArmando] = useState(false);
  const [marcando, setMarcando] = useState(false);
  // Hay vehículo vinculado pero todavía "en evaluación" (no lo compró) => no cuenta como comprado
  const enEvaluacion = !!chequeo.id_vehiculo && chequeo.estado_vehiculo === 'en_evaluacion';
  const comprado = !!chequeo.id_vehiculo && !enEvaluacion;
  const [editando, setEditando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const marcarComprado = async () => {
    setMarcando(true);
    try {
      await api(`/chequeos/${chequeo.id}/marcar-comprado`, { method: 'POST' });
      const actualizado = await api(`/chequeos/${chequeo.id}`);
      onActualizado(actualizado);
      alert('Listo: el vehículo quedó como comprado en la ficha del cliente.');
    } catch {
      alert('No se pudo marcar el vehículo como comprado.');
    }
    setMarcando(false);
  };

  const armarPresupuesto = async () => {
    setArmando(true);
    try {
      const res = await api(`/chequeos/${chequeo.id}/armar-presupuesto`, { method: 'POST' });
      navigate('/presupuestos', {
        state: {
          crear: true,
          clientePreseleccionado: res.cliente,
          vehiculoPreseleccionado: res.vehiculo,
          paquetesIniciales: res.paquetes.map(p => ({
            nombre: p.nombre, precio: 0,
            tareas: p.tareas,
            repuestos: [],
            costo_repuestos: 0, costo_mano_obra: 0, comision_ml_pct: 15, iva_pct: 22,
          })),
        }
      });
    } catch (e) {
      alert(e.message || 'No se pudo armar el presupuesto.');
    }
    setArmando(false);
  };

  const cerrarChequeo = async () => {
    setCambiandoEstado(true);
    try {
      await api(`/chequeos/${chequeo.id}/cerrar`, { method: 'POST' });
      const actualizado = await api(`/chequeos/${chequeo.id}`);
      onActualizado(actualizado);
    } catch {
      alert('No se pudo cerrar el chequeo.');
    }
    setCambiandoEstado(false);
  };

  const reabrirChequeo = async () => {
    setCambiandoEstado(true);
    try {
      await api(`/chequeos/${chequeo.id}/reabrir`, { method: 'POST' });
      const actualizado = await api(`/chequeos/${chequeo.id}`);
      onActualizado(actualizado);
    } catch {
      alert('No se pudo reabrir el chequeo.');
    }
    setCambiandoEstado(false);
  };

  if (editando) {
    return (
      <EditarChequeo
        chequeo={chequeo}
        onGuardado={async () => {
          const actualizado = await api(`/chequeos/${chequeo.id}`);
          setEditando(false);
          onActualizado(actualizado);
        }}
        onCancelar={() => setEditando(false)}
      />
    );
  }

  const categorias = [...new Set(chequeo.items.map(i => i.categoria))];

  return (
    <div className="page-content">
      <div className="sec-header" style={{ flexWrap: 'wrap', gap: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <h2 className="page-title" style={{ marginBottom: 0 }}>{chequeo.id}</h2>
          {chequeo.cerrado
            ? <span className="badge badge-gris">Cerrada</span>
            : <span className="badge badge-verde">Abierta</span>}
        </div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={onVolver}>← Volver</button>
          <button className="btn btn-outline btn-sm" onClick={() => setEditando(true)}>✏️ Editar</button>
          <a href={apiUrl(`/chequeos/${chequeo.id}/pdf-formulario`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
            🖨️ Formulario en blanco
          </a>
          <a href={apiUrl(`/chequeos/${chequeo.id}/pdf`)} className="btn btn-outline btn-sm" target="_blank" rel="noreferrer">
            📄 Ver PDF resultado
          </a>
          {chequeo.cerrado ? (
            <button className="btn btn-outline btn-sm" disabled={cambiandoEstado} onClick={reabrirChequeo}>
              {cambiandoEstado ? '...' : '↺ Reabrir'}
            </button>
          ) : (
            <button className="btn btn-outline btn-sm" disabled={cambiandoEstado} onClick={cerrarChequeo}>
              {cambiandoEstado ? '...' : '🔒 Cerrar chequeo'}
            </button>
          )}
          <button className="btn btn-outline btn-sm" disabled={armando} onClick={armarPresupuesto}>
            {armando ? 'Leyendo observaciones...' : '📋 Armar presupuesto'}
          </button>
          {!comprado && (
            <button className="btn btn-success btn-sm" disabled={marcando} onClick={marcarComprado}>
              {marcando ? 'Guardando...' : '🚗 Compró el vehículo'}
            </button>
          )}
          {enEvaluacion && <span className="badge badge-gris">Vehículo en evaluación</span>}
          {comprado && <span className="badge badge-verde">Vehículo creado</span>}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="resumen-grid">
          <div className="resumen-card"><div className="label">Interesado</div><div className="valor">{chequeo.nombre_cliente}</div></div>
          <div className="resumen-card"><div className="label">Celular</div><div className="valor">{chequeo.celular || '—'}</div></div>
          <div className="resumen-card"><div className="label">RUT o CI</div><div className="valor">{chequeo.rut || '—'}</div></div>
          <div className="resumen-card"><div className="label">Dirección</div><div className="valor">{chequeo.direccion || '—'}</div></div>
          <div className="resumen-card"><div className="label">Fecha</div><div className="valor">{fmtFecha(chequeo.fecha)}</div></div>
        </div>
      </div>

      {(chequeo.vendedor_nombre || chequeo.vendedor_contacto) && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vendedor</p>
          <div className="resumen-grid">
            <div className="resumen-card"><div className="label">Nombre</div><div className="valor">{chequeo.vendedor_nombre || '—'}</div></div>
            <div className="resumen-card"><div className="label">Contacto</div><div className="valor">{chequeo.vendedor_contacto || '—'}</div></div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Datos del vehículo</p>
        <div className="resumen-grid">
          <div className="resumen-card"><div className="label">Marca</div><div className="valor">{chequeo.marca || '—'}</div></div>
          <div className="resumen-card"><div className="label">Modelo</div><div className="valor">{chequeo.modelo || '—'}</div></div>
          <div className="resumen-card"><div className="label">Año</div><div className="valor">{chequeo.anio || '—'}</div></div>
          <div className="resumen-card"><div className="label">Versión</div><div className="valor">{chequeo.version || '—'}</div></div>
          <div className="resumen-card"><div className="label">Color</div><div className="valor">{chequeo.color || '—'}</div></div>
          <div className="resumen-card"><div className="label">Kilometraje</div><div className="valor">{chequeo.kilometraje || '—'}</div></div>
          <div className="resumen-card"><div className="label">Combustible</div><div className="valor">{chequeo.combustible || '—'}</div></div>
          <div className="resumen-card"><div className="label">Transmisión</div><div className="valor">{chequeo.transmision || '—'}</div></div>
          <div className="resumen-card"><div className="label">Motor</div><div className="valor">{chequeo.motor || '—'}</div></div>
          <div className="resumen-card"><div className="label">Admisión</div><div className="valor">{chequeo.admision || '—'}</div></div>
          <div className="resumen-card"><div className="label">Matrícula</div><div className="valor">{chequeo.matricula || '—'}</div></div>
          <div className="resumen-card"><div className="label">Padrón</div><div className="valor">{chequeo.padron || '—'}</div></div>
          <div className="resumen-card"><div className="label">N° de chasis</div><div className="valor">{chequeo.n_chasis || '—'}</div></div>
          <div className="resumen-card"><div className="label">N° de motor</div><div className="valor">{chequeo.n_motor || '—'}</div></div>
        </div>
        {chequeo.ultimo_service && (
          <p style={{ marginTop: '.6rem', fontSize: '.875rem' }}><b>Último service/reparaciones:</b> {chequeo.ultimo_service}</p>
        )}
      </div>

      {categorias.map(categoria => (
        <div className="card" key={categoria} style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>{categoria}</p>
          {chequeo.items.filter(i => i.categoria === categoria).map((it, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8125rem',
                                    padding: '.3rem 0', borderBottom: '1px solid var(--gris-2)' }}>
              <span>{it.sistema}</span>
              <span style={{ fontWeight: 600 }}>{it.estado || '—'} {it.observacion ? `· ${it.observacion}` : ''}</span>
            </div>
          ))}
        </div>
      ))}

      {chequeo.observaciones_generales && (
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--azul)', marginBottom: '.5rem' }}>Observaciones generales</p>
          <p style={{ fontSize: '.875rem', whiteSpace: 'pre-wrap' }}>{chequeo.observaciones_generales}</p>
        </div>
      )}
    </div>
  );
}
